/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

import introJs from 'intro.js';

import { ApiService } from './api.service';
import { UserService } from './user.service';

export interface Tour {
  path: string;
  tourOptions: any;
  completed?: boolean;
  cancelled?: boolean;
  autoStart?: boolean;
  group?: string;
  products: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  public tourMenu = signal(true);

  private router = inject(Router);
  private document = inject(DOCUMENT);
  private apiService = inject(ApiService);

  private userService = inject(UserService);
  readonly user = this.userService.state;
  readonly userTours = this.userService.userTours;
  readonly isClientAdmin = computed(() => this.userService.hasClaim('client-admin'));

  introJs: any;

  dashboard: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Thanks for visiting LakeHouse!',
        intro: 'Here’s a brief tour of the Dashboard page.'
      },
      {
        element: '#tour-nav-main',
        position: 'right',
        title: 'LakeHouse Products',
        intro: 'The Dashboard is your home page.  The main menu displays links to the Dashboard as well as other products in your subscription.'
      },
      {
        element: '#tour-nav-util',
        position: 'right',
        title: 'News & Messages',
        intro: 'Access news and messages here.  The News link will display the latest industry News.  Messages share important notifications from LakeHouse about new releases, system upgrades, and webinars.'
      },
      {
        element: '#tour-nav-user',
        position: 'right',
        title: 'User Profile',
        intro: 'Click on your name to access your user profile or log out of LakeHouse.'
      },
      {
        element: '#tour-dashboard-pc',
        position: 'right',
        title: 'Summaries and Shortcuts',
        intro: 'The dashboard also contains data summaries and links for your LakeHouse products. '
      },
      {
        element: '#tour-nav-tour',
        position: 'right',
        title: 'Getting Help',
        intro: 'If you have questions about this or any other LakeHouse page, find the <tour> link to access the instructional tour.'
      }
    ]
  }
  balanceSheet: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Balance Sheet',
        intro: 'Use the controls above the graphs to navigate the Balance Sheet.'
      },
      {
        element: '#tour-balance-date',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the slider to select the quarter you would like to analyze.'
      },
      {
        element: '#tour-balance-tabs',
        position: 'bottom',
        title: 'Select a Category',
        intro: 'Use the tabs to select the type of financial transactions to review.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select a Bank or Peer Group',
        intro: '<p>Click on the peer selector to choose a peer group or search for an individual bank.</p><p>If you have selected a custom peer group, click the funnel icon next to the group name to display and filter group members.  (Aggregate groups cannot be filtered since their group averages are precalculated.)</p>'
      },
      {
        element: '#tour-row',
        position: 'right',
        title: 'Adjust the Layout',
        intro: 'Click the up or down carets to expand or collapse each page section.'
      },
      {
        element: '#tour-print',
        position: 'bottom',
        title: 'Print',
        intro: '<p>Click the printer icon to create a PDF copy of the report.  The report will include the expanded sections on the page. Expand wanted or collapse unwanted page sections before clicking on the printer icon.</p><p>Additionally, you can include individual members of the selected peer group and/or include the graphs information box by clicking on the respective check boxes.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-feedback',
        position: 'left',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  creditQuality: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Credit Quality',
        intro: 'Explore Credit Quality across your loan portfolio. '
      },
      {
        element: '#tour-balance-date',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the slider to select the quarter you would like to analyze.'
      },
      {
        element: '#tour-balance-tabs',
        position: 'bottom',
        title: 'Select a Loan Segment',
        intro: 'Use the tabs to navigate through your loan portfolio.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select a Bank or Peer Group',
        intro: '<p>Click on the peer selector to choose a peer group or search for an individual bank.</p><p>If you have selected a custom peer group, click the funnel icon next to the group name to display and filter group members.  (Aggregate groups cannot be filtered since their group averages are precalculated.)</p>'
      },
      {
        element: '#tour-row',
        position: 'right',
        title: 'Adjust the Layout',
        intro: 'Click the up or down carets to expand or collapse each page section.'
      },
      {
        element: '#tour-print',
        position: 'bottom',
        title: 'Print',
        intro: '<p>Click the printer icon to create a PDF copy of the report.  The report will include the expanded sections on the page. Expand wanted or collapse unwanted page sections before clicking on the printer icon.</p><p>Additionally, you can include individual members of the selected peer group and/or include the graphs information box by clicking on the respective check boxes.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-feedback',
        position: 'left',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  trend: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Trends',
        intro: 'Use the controls above the graphs to direct your exploration of historical performance.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select a Bank or Peer Group',
        intro: '<p>Click on the peer selector to choose a peer group or search for an individual bank.</p><p>If you have selected a custom peer group, click the funnel icon next to the group name to display and filter group members.  (Aggregate groups cannot be filtered since their group averages are precalculated.)</p>'
      },
      {
        element: '#tour-date-range-selector',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the sliding buttons to mark the start and end of the period you would like to analyze.'
      },
      {
        element: '#tour-metric-selector',
        position: 'right',
        title: 'Select Metrics',
        intro: 'Select a metric to display on each graph: browse the metrics or search by name.'
      },
      {
        element: '#tour-legend',
        position: 'top',
        title: 'Legend',
        intro: 'The legend identifies your organization and your peer group. The group members are not listed unless you have filtered the group to just one bank.'
      },
      {
        element: '#tour-pdf',
        position: 'bottom',
        title: 'Print',
        intro: '<p>When creating a PDF copy of the report, you have the option to list the individual members of the selected peer group and/or include the graphs information box.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-nav-groups',
        position: 'left',
        title: 'Manage Peer Groups',
        intro: 'Learn more about existing peer groups and create custom groups that you can use throughout the Peer Comparison module.'
      },
      {
        element: '#tour-feedback',
        position: 'left',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  quarterly: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Quarterly',
        intro: 'Compare your quarterly performance metrics to  to those of your peers.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select Competitors or a Peer Group',
        intro: '<p>Click the <strong>Competitor Watch</strong> tab and select up to 3 banks to show a side by side comparison for the selected date, or click the <strong>Group Performance</strong> tab and select a peer group to compare your metrics to peer group averages.</p><p>You can filter to a subset of the selected group by clicking the funnel icon next to the group name.  (Note: group averages are precalculated for aggregate groups; these groups cannot be filtered.)</p>'
      },
      {
        element: '#tour-first-row',
        position: 'bottom',
        title: 'Trends & Percentiles',
        intro: 'Click a metric in the Quarterly Summary table to display graphs of your bank’s historical performance and percentile within the selected group.'
      },
      {
        element: '#tour-pdf',
        position: 'bottom',
        title: 'Print',
        intro: '<p>When creating a PDF copy of the report, you have the option to list the individual members of the selected peer group and/or include the graphs information box.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-nav-groups',
        position: 'left',
        title: 'Manage peer groups',
        intro: 'Learn more about existing peer groups and create custom groups that you can use throughout the Peer Comparison module.'
      },
      {
        element: '#tour-feedback',
        position: 'left',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  correlation: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Correlations',
        intro: 'Explore the relationship between your bank’s performance and US economic indicators.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select a Bank or Peer Group',
        intro: 'Click in the peer selector to choose a peer group, filter the peer group, or search for an individual bank.'
      },
      {
        element: '#tour-date-range-selector',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the sliding buttons to mark the start and end of the period you would like to analyze.'
      },
      {
        element: '#tour-rate-selector',
        position: 'left',
        title: 'Select an Economic Indicator',
        intro: 'Select a rate that could be a good predictor of your bank’s performance.  Choose a lagged rate if you think that current rate fluctuations may affect your performance 1 or 2 quarters later.'
      },
      {
        element: '#tour-legend',
        position: 'top',
        title: 'Legend',
        intro: 'The legend identifies your organization and your peer group. If you have filtered to a subset of the peer group, please note that the legend does not list the individual banks unless the subset contains just one bank.'
      },
      {
        element: '#tour-correlation-numbers',
        position: 'left',
        title: 'Evaluate the Model',
        intro: '<p>The statistics describe the linear relationship between the selected rate and selected performance metric.</p><p>&#946;: How is performance impacted by a 1 unit change in the rate?</p><p>R<sup>2</sup>: How much of the variability in performance might be explained by variability in the selected rate?</p><p>It is not possible to draw conclusions from what you observe over a short timeframe. (No statistics are reported if you select less than 3 quarters.)</p>'
      },
      {
        element: '#tour-pdf',
        position: 'bottom',
        title: 'Print',
        intro: '<p>When creating a PDF copy of the report, you have the option to list the individual members of the selected peer group and/or include the graphs information box.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-nav-groups',
        position: 'left',
        title: 'Manage Peer Groups',
        intro: 'Learn more about existing peer groups and create custom groups that you can use throughout the Peer Comparison module.'
      },
      {
        element: '#tour-feedback',
        position: 'left',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  performance: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Performance',
        intro: 'Use the controls above the graphs to navigate the performance reports.'
      },
      {
        element: '#tour-performance-tabs',
        position: 'bottom',
        title: 'Select a Category',
        intro: 'Use the tabs to select the performance category or choose Snapshot to see your bank’s composite ranking relative to banks in the Benchmark Peer Group.'
      },
      {
        element: '#tour-peer-selector',
        position: 'right',
        title: 'Select a Bank or Peer Group',
        intro: '<p>Use the peer selector to choose a peer group or search for an individual bank.</p><p>If you have selected a custom peer group, clicking the funnel icon next to the group name will display the group members and allow you to filter the report.  (Aggregate groups cannot be filtered since their group averages are precalculated.)</p>'
      },
      {
        element: '#tour-date-range-selector',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the sliding buttons to mark the start and end of the period you would like to analyze.'
      },
      {
        element: '#tour-legend',
        position: 'top',
        title: 'Legend',
        intro: 'The legend identifies your organization and your peer group. The group members are not listed unless you have filtered the group to just one bank.'
      },
      {
        element: '#tour-pdf',
        position: 'bottom',
        title: 'Print',
        intro: '<p>When creating a PDF copy of the report, you have the option to list the individual members of the selected peer group and/or include the graphs information box.</p><p>If you would like to see your bank’s logo on the report, reach out to your bank’s LakeHouse administrator.</p><p>For best results, use Chrome or Microsoft Edge.</p>'
      },
      {
        element: '#tour-nav-groups',
        position: 'left',
        title: 'Manage Peer Groups',
        intro: 'Learn more about existing peer groups and create custom groups that you can use throughout the Peer Comparison module.'
      },
      {
        element: '#tour-feedback',
        position: 'top',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  groups: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Peer Groups',
        intro: 'The Groups page lists the peer groups available within Peer Comparison.'
      },
      {
        element: '#tour-groups-view',
        position: 'left',
        title: 'View or Edit the Member List',
        intro: 'Click the view button to list the banks that compose each group.'
      },
      {
        element: '#tour-groups-create',
        position: 'right',
        title: 'Create a New Group',
        intro: 'Search to find banks similar to yours or banks with targeted attributes.  You can also build a group of banks selected by name. '
      }
    ]
  }
  createSimilar: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Similar Banks',
        intro: 'An automated search identifies banks that are similar to yours in terms of loans balances, deposit balances, and overall assets. The search routine attempts to find a small group, but one having 8 or more banks.'
      },
      {
        element: '#tour-group-create-date',
        position: 'bottom',
        title: 'Select the Reporting Period',
        intro: 'Use the slider to select the reporting date.'
      },
      {
        element: '#tour-group-create-state',
        position: 'left',
        title: 'Narrow the Search by State (Optional)',
        intro: 'You can geographically limit the search to a single state.  Alternatively, if you need to add or remove banks outside of your region, you can edit the group membership after saving it.'
      },
      {
        element: '#tour-group-create-list',
        position: 'top',
        title: 'Review the Banks Selected',
        intro: 'The list of selected banks automatically updates as the filter criteria are changed.'
      },
      {
        element: '#tour-group-create-actions',
        position: 'top',
        title: 'Discard or Save the Group',
        intro: 'Click Create Group to save your group. Or click Discard to cancel or use a different group selection tool.'
      }
    ]
  }
  createTargeted: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Targeted Banks',
        intro: 'Create a group by defining performance criteria for group selection.'
      },
      {
        element: '#tour-group-create-date',
        position: 'bottom',
        title: 'Select the Reporting Period',
        intro: 'Use the slider to select the reporting date.'
      },
      {
        element: '#tour-group-targeted-primary',
        position: 'top',
        title: 'Primary Selection Criteria',
        intro: 'Click the checkbox to filter on asset range, asset growth, and/or loan to deposit ratio.  Enter the desired lower and upper bounds for these metrics.'
      },
      {
        element: '#tour-group-targeted-profile',
        position: 'top',
        title: 'Narrow Results by Bank Demographics',
        intro: 'You can limit the search by type of ownership and/or state (state of filing HQ).  Use the drop-down lists to select one or more elements.'
      },
      {
        element: '#tour-group-targeted-services',
        position: 'top',
        title: 'Narrow Results by Bank Services',
        intro: 'Limit group membership to institutions with a concentration of at least 25% in each selected loan area and/or institutions that offer specific services (student loans, credit card, etc.  Note:  services are inferred from call data report; they are not explicitly reported.)'
      },
      {
        element: '#tour-group-targeted-metrics',
        position: 'top',
        title: 'Additional Performance Criteria',
        intro: '<p>Choose from additional performance metrics to fine tune the selection criteria.</p><p>Use these metrics to identify banks that are performing better than your bank or banks whose performance is within a specified range.</p>'
      },
      {
        element: '#tour-group-create-list',
        position: 'top',
        title: 'Review the Banks Selected',
        intro: 'The list of selected banks automatically updates as the filter criteria are changed.'
      },
      {
        element: '#tour-group-create-actions',
        position: 'top',
        title: 'Discard or Save the Group',
        intro: 'Click Create Group to save your group. Or click Discard to cancel or use a different group selection tool. Note that groups with more than 100 members cannot be saved.'
      }
    ]
  }
  userAdministration: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Administration',
        intro: 'Use the Administration controls to manage user permissions for your organization and to customize PDF reports.'
      },
      {
        element: '#tour-admin-logo',
        position: 'bottom',
        title: 'Logo',
        intro: 'Add your logo to the PDF reports from LakeHouse. To replace a logo, first delete the existing logo.'
      },
      {
        element: '#tour-admin-users',
        position: 'top',
        title: 'User Administration',
        intro: 'The table lists all LakeHouse users from your organization. To modify a user’s permissions or delete a user, move the mouse pointer over their name to find the Edit and Delete links.'
      },
      {
        element: '#tour-admin-invite',
        position: 'left',
        title: 'Add  a User',
        intro: 'Email an invitation to a new user by specifying their name and email address. You’ll also need to indicate which LakeHouse products the user can access, this includes LakeHouse User Administration.'
      },
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Administration Reference Guide',
        intro: 'For more information about LakeHouse Administration, please refer to the <a href="/api/image/Client User Administration Quick Start.pdf" style="color: white; text-decoration: underline;" target="_blank">Client User Administration Quick Start Guide.</a>'
      }
    ]
  }

  balanceDecay: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Balance Decay',
        intro: 'Use the controls above the graphs to navigate the Balance Decay.'
      },
      {
        element: '#tour-balance-date',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the slider to select the quarter you would like to analyze.'
      },
      {
        element: '#tour-decay-category',
        position: 'bottom',
        title: 'Select a Category',
        intro: 'Use the tabs to select the type of Deposit Balance category to review.'
      },
      {
        element: '#tour-row-decay',
        position: 'right',
        title: 'Adjust the Layout',
        intro: 'Click the up or down carets to expand or collapse each section.'
      },
      {
        element: '#tour-year-truncation',
        position: 'bottom',
        title: '10 Year Truncation',
        intro: 'When enabled the truncation point limits the term—or full life—of the deposit to 10 years, which ensures that the weighted average life (WAL) remains below 10 years. This approach prevents the model from extending the WAL beyond a reasonable horizon for non-maturity deposit assumptions.'
      },
      {
        element: '#tour-covid-truncation',
        position: 'bottom',
        title: 'Adjust for Covid?',
        intro: 'When on, COVID growth is treated as unstable, with most balances expected to run off quickly. When off, growth is assumed to behave like a bank merger, with balances largely retained.'
      },
      {
        element: '#tour-print',
        position: 'bottom',
        title: 'Print PDF',
        intro: 'Create a PDF copy of the report.  The PDF will include the only expanded sections of the report. To include your bank’s logo on the report, reach out to your LakeHouse administrator.'
      },
      {
        element: '#tour-feedback',
        position: 'bottom',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }

  betaRegressions: any = {
    scrollToElement: true,
    steps: [
      {
        tooltipClass: 'introjs-tooltip--wide',
        title: 'Correlations',
        intro: 'Explore the relationship between your bank’s Deposit Interest rates and key US market rates.'
      },
      {
        element: '#tour-balance-date',
        position: 'bottom',
        title: 'Define the Reporting Period',
        intro: 'Use the slider to select the quarter you would like to analyze.'
      },
      {
        element: '#tour-metric-selector',
        position: 'bottom',
        title: 'Select a Deposit Rate',
        intro: 'Click on Metric Drop down arrow and select the deposit rate you would like to analyze.'
      },
      {
        element: '#tour-rate-selector',
        position: 'bottom',
        title: 'Select an Economic Indicator',
        intro: 'Select a rate that could be a good predictor of your bank’s performance.  Choose a lagged rate if you think that current rate fluctuations may affect your performance 1 or 2 quarters later.'
      },
      {
        element: '#tour-regression-chart',
        position: 'left',
        title: 'Linear Regression Equation',
        intro: 'Represents the best-fit line between your selected Market Rate (independent variable) and the observed Deposit Rate (dependent variable), helping reveal trends and make predictions'
      },
      {
        element: '#tour-correlation-numbers',
        position: 'left',
        title: 'Evaluate the Model',
        intro: '<p>The statistics describe the linear relationship between the selected rate and selected performance metric.</p><p>&#946;: How is performance impacted by a 1 unit change in the rate?</p><p>R<sup>2</sup>: How much of the variability in performance might be explained by variability in the selected rate?</p><p>It is not possible to draw conclusions from what you observe over a short timeframe. (No statistics are reported if you select less than 3 quarters.)</p>'
      },
      {
        element: '#tour-print',
        position: 'bottom',
        title: 'Print PDF',
        intro: 'Create a PDF copy of the report.  The PDF will include the only expanded sections of the report. To include your bank’s logo on the report, reach out to your LakeHouse administrator.'
      },
      {
        element: '#tour-feedback',
        position: 'bottom',
        title: 'Provide Feedback',
        intro: 'If you would like to see additional metrics or report features, submit your suggestions via the feedback button.'
      }
    ]
  }
  toursInit = false;
  tours: Tour[] = [
    {
      path: '/dashboard',
      tourOptions: this.dashboard,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'dashboard',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/balance/snapshot',
      tourOptions: this.balanceSheet,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'balanceSheet',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/balance/loans',
      tourOptions: this.balanceSheet,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'balanceSheet',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/balance/investments',
      tourOptions: this.balanceSheet,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'balanceSheet',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/balance/deposits',
      tourOptions: this.balanceSheet,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'balanceSheet',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/balance/borrowings',
      tourOptions: this.balanceSheet,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'balanceSheet',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/pdna',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/corecov',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/agriculture',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/commercial',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/construction',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/consumer',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/cre',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/lease',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/multifamily',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/residential',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/creditquality/other',
      tourOptions: this.creditQuality,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'creditQuality',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/trends',
      tourOptions: this.trend,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/quarterly/group',
      tourOptions: this.quarterly,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/quarterly/institution',
      tourOptions: this.quarterly,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/correlations',
      tourOptions: this.correlation,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/snapshot',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/liquidity',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/profitability',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/capital',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/revenue',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/performance/credit',
      tourOptions: this.performance,
      completed: false,
      cancelled: false,
      autoStart: true,
      group: 'action',
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/groups/manage',
      tourOptions: this.groups,
      completed: false,
      cancelled: false,
      autoStart: false,
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/peer-comparison/groups/create?type=similar',
      tourOptions: this.createSimilar,
      completed: false,
      cancelled: false,
      autoStart: false,
      products: ['peer-comparison-standard']
    },
    {
      path: '/peer-comparison/groups/create?type=targeted',
      tourOptions: this.createTargeted,
      completed: false,
      cancelled: false,
      autoStart: false,
      products: ['peer-comparison-standard']
    },
    {
      path: '/account/admin',
      tourOptions: this.userAdministration,
      completed: false,
      cancelled: false,
      autoStart: false,
      products: ['peer-comparison-basic', 'peer-comparison-standard']
    },
    {
      path: '/non-maturity-deposits/decay_basic',
      tourOptions: this.balanceDecay,
      completed: false,
      cancelled: false,
      autoStart: true,
      products: ['non-maturity-deposits-standard']
    },
    {
      path: '/non-maturity-deposits/beta_reg/beta_basic',
      tourOptions: this.betaRegressions,
      completed: false,
      cancelled: false,
      autoStart: true,
      products: ['non-maturity-deposits-standard']
    },
        {
      path: '/non-maturity-deposits/beta_reg/beta_linear',
      tourOptions: this.betaRegressions,
      completed: false,
      cancelled: false,
      autoStart: true,
      products: ['non-maturity-deposits-standard']
    },
  ]

  constructor() {
    effect(() => {
      this.user();
      const userTours = this.userTours();
      if (this.userService.isAuthenticated()) {
        if (userTours != undefined) {
          this.initTours(userTours);
        }
      }
    });
  }

  initTours(userTours: Tour[]) {
    this.tours.forEach(tour => {
      const userTour = userTours.find(t => t.path == tour.path);
      if (userTour != undefined) {
        tour.completed = userTour.completed;
        tour.cancelled = userTour.cancelled;
        if (tour.completed || tour.cancelled) {
          this.tours.forEach(groupTour => {
            if (groupTour.group == tour.group) {
              groupTour.autoStart = false;
            }
          });
        }
      }
    });

    const dashboardTour = this.tours.find(t => t.path == '/dashboard');
    if (dashboardTour != undefined && this.isClientAdmin()) {
      dashboardTour.tourOptions.steps[3].title = 'User Administration';
      dashboardTour.tourOptions.steps[3].intro = 'Click on your name to access your user profile or log out of LakeHouse. As a LakeHouse administrator, you will also see a link to the Administration page where you can manage users from your organization and upload a logo for use in PDF reports.';
    }

    //const userAdministrationTour = this.tours.find(t => t.path == '/account/admin');
    //if (userAdministrationTour != undefined && this.authService.hasProduct('PeerComparisonBasic')) {
    //  userAdministrationTour.tourOptions.steps.splice(1, 1);
    //}

    this.toursInit = true;
    this.checkTour(this.router.url);
  }

  checkTour(path: string) {
    if (this.toursInit) {
      const tour = this.tours.find(t => t.path == path);
      if (tour && tour.products.some(t => this.user().products.find(p => p.tierClientCode == t))) {
        setTimeout(() => {
          this.tourMenu.set(true);
          if (tour.autoStart) {
            setTimeout(() => {
              this.startTour(path);
            });
          }
        });
      }
      else {
        setTimeout(() => {
          this.tourMenu.set(false);
        });
      }
    }
  }

  filterTour(options: any): any {
    return options.steps.filter((step: any) => {
      if (step.element != undefined && !this.document.getElementById(step.element.substring(1))) {
        console.log('Element not found:', step.element);
        return false;
      }
      return true;
    });
  }

  public startTour(path: string) {
    const tour = this.tours.find(t => t.path == path);
    if (tour) {
      this.introJs = introJs.tour();
      this.introJs.oncomplete(() => {
        tour.completed = true;
        tour.cancelled = false;
      });
      this.introJs.onexit(() => {
        if (!tour.completed) {
          tour.completed = false;
          tour.cancelled = true;
        }
        for (const t of this.tours.filter(t => t.group == tour.group)) {
          t.autoStart = false;
        }
        this.updateTour(path, tour.completed!, tour.cancelled!).subscribe(tours => {
          this.userService.setTours(tours);
        });
      });

      // Filter out steps that are missing elements
      // disabled for now
      //const filtered = this.filterTour(tour.tourOptions);

      const filtered = tour.tourOptions.steps;
      this.introJs
        .setOptions({ scrollToElement: true, steps: filtered })
        .start();
    }
  }

  public updateTour(path: string, completed: boolean, cancelled: boolean): Observable<Tour[]> {
    return this.apiService.put<Tour[]>('api/tour', { path, completed, cancelled });
  }
}
